import React, { useState, useEffect, useCallback } from 'react'
import { googleApiKey, s3Static } from '../../../../utils/constants'
import Image from 'next/image'
import Link from 'next/link'
import { ptSingaporeFaqs } from '../../../../utils/faq'
import { DarkFaq } from './DarkFaq'
import { openModal } from 'service/modal-service'
import { useRouter } from 'next/router'
import { RequestProvider } from 'context/RequestContext'

// Section Components
import { TrainerCustomerReviews } from './TrainerCustomerReviews'
import { PersonalTrainerWhyUs } from './PersonalTrainerWhyUs'
import { TopTrainersSection } from './TopTrainersSection'
import { FaCheckCircle } from 'react-icons/fa'
import { PersonalTrainerHead } from './PersonalTrainerHead'
import { HERO_TRAINER_COUNT } from './HeroTrainerStrip'
import { PersonalTrainerBooking } from './PersonalTrainerBooking'
import Script from 'next/script'
import { SuccessStories } from './SuccessStories'
import TrainerValueProposition from './TrainerValueProposition'
import { PtCostAndChoosing } from './PtCostAndChoosing'

// NOTE: A `HowToFindTrainerSection` component ("Simple 4-Step Process" / "How It Works")
// used to be defined here but was never rendered anywhere in this file. It has been
// removed as part of the mobile fold + JS payload reduction pass. Recover from git
// history if the steps content is ever needed again.
// Main Component
export const PersonalTrainer = (props) => {
    const { requestCategorySettings, spList, spListMap, spLocationList, localityList } = props;
    // Falls back to the live roster length if the page didn't supply an explicit count.
    const trainerCount = props.trainerCount || (Array.isArray(spListMap) ? spListMap.length : 0)
    const router = useRouter()
    
    // The Google Maps bundle is a large third-party script for a widget that sits
    // several folds down, and it plants ~190 markers. Load it only once the map
    // actually scrolls into view, so visitors who never reach it never pay for it.
    const [isMapVisible, setIsMapVisible] = useState(false)
    const [requestForm, setRequestForm] = useState(false)
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)
    
    const requestType = "Fitness Trainer"

    useEffect(() => {
        if (router.query.requestForm === "true") {
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const initMap = useCallback( async() =>  {        
        const map = new google.maps.Map(document.getElementById('map'), {
            zoom: 11,
            center: new google.maps.LatLng(1.3521, 103.8198),
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            streetViewControl: false,
            mapTypeControl: false,
            mapId: "b76338e454946df6"
        });
        
        const infoWindow = new google.maps.InfoWindow();

        let i;
        for (i = 0; i < spLocationList.length; i++) {  
            const position = new google.maps.LatLng(spLocationList[i][1], spLocationList[i][2]);

            // Create a mini avatar card as marker content
            const markerContent = document.createElement('div');
            markerContent.style.display = 'flex';
            markerContent.style.flexDirection = 'column';
            markerContent.style.alignItems = 'center';
            markerContent.style.textAlign = 'center';
            markerContent.style.backgroundColor = 'transparent';
            markerContent.style.fontFamily = 'sans-serif';

            const img = document.createElement('img');
            img.src = "/personal-trainer.png";
            img.style.width = '20px';
            img.style.height = '30px';
            img.style.borderRadius = '50%';
            img.style.objectFit = 'cover';
            img.alt = 'Trainer avatar';

            markerContent.appendChild(img);
            markerContent.style.transition = 'transform 0.2s ease';
            markerContent.addEventListener('mouseenter', () => {
            markerContent.style.transform = 'scale(1.05)';
            });
            markerContent.addEventListener('mouseleave', () => {
            markerContent.style.transform = 'scale(1)';
            });

            const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            content: markerContent,
            });

            const contentString =
                    '<div id="content">' +
                    '<div id="siteNotice">' +
                    "</div>" +
                    '<h3 id="firstHeading" class="text-center text-base font-bold pb-2">' + spListMap[i].userFirstName + '</h3>' +
                    '<div id="bodyContent" class="w-40">' +                    
                    "<div class='flex'><img class='rounded-lg w-full' src='" + spListMap[i].profilePic + "' layout='fill' height='140px' alt='profile image'/>" +
                    "</div>" + 
                    "<div class='text-center text-xs'>" + spListMap[i].userLocation + "</div>" +
                    "</div>" +
                    "<div class='text-center py-2'><a class='text-orange-600 font-bold' target='_blank' rel='noreferrer' href='/profile/" + spListMap[i].handleName + "'>View Profile</a></div>" +
                    "</div>" +
                    "</div>";    

            marker.addListener('gmp-click', () => {
                infoWindow.setContent(contentString);
                infoWindow.open(map);
                infoWindow.setPosition(position); // 👈 anchor by LatLng, not marker
            });
        }
    }, [spListMap, spLocationList])
    
    useEffect(() => {
        window.initMap = () => {
            initMap(); // assign for Google callback
        };

        if (window.google && window.google.maps) {
            initMap(); // call directly if already loaded
        }
    }, [initMap]);

    // Gate the Maps <Script> on the map container entering the viewport.
    // rootMargin starts the fetch slightly before it is reached so the map is
    // usually painted by the time the user gets there. Browsers without
    // IntersectionObserver fall back to loading immediately.
    useEffect(() => {
        if (isMapVisible) return
        const el = document.getElementById('map')
        if (!el || typeof IntersectionObserver === 'undefined') {
            setIsMapVisible(true)
            return
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                setIsMapVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '400px' })
        observer.observe(el)
        return () => observer.disconnect()
    }, [isMapVisible]);

    const trainerDensity = [
        { region: 'East', trainerPercent: 30 },
        { region: 'West', trainerPercent: 10 },
        { region: 'Central', trainerPercent: 40 },
        { region: 'North', trainerPercent: 20 }
    ];

    return (
        <>
            <RequestProvider>
                {/* 1. Hero Section */}
                <PersonalTrainerHead
                    heroImage={s3Static + "personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"}
                    title="Personal Trainer in Singapore"
                    heading={`Personal Trainer Singapore — ${trainerCount} Certified Trainers at Home, Condo Gym or Park`}
                    heroCopy="Browse certified trainers near you, or let us match you. Train at your home, condo gym or a nearby park."
                    spList={spList}
                    trainerCount={trainerCount}
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />   
                
                <section className="relative bg-slate-900 py-10 md:py-16 overflow-hidden">
                    {/* Mesh gradient */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />
                    
                    <div className="relative z-10 max-w-5xl mx-auto px-4">
                    {/* Header */}
                    {/* <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6 text-center tracking-tight">
                        Looking for a Personal Trainer in Singapore?
                    </h2>  */}
                    {/* Two Column Layout */}
                    <div className="grid lg:grid-cols-2 gap-4 md:gap-8 items-center">
                        {/* Intro copy. Previously two long paragraphs plus a three-item bullet
                            list; the bullets duplicated the match criteria already covered by
                            TrainerValueProposition, so they are now an inline chip row. */}
                        <div className='space-y-4'>
                            <p className="text-base md:text-lg text-white/60 leading-relaxed">
                                StringsSG is a Singapore-registered personal training company employing <strong className="text-white/90">{trainerCount} certified
                                personal trainers</strong> island-wide. We hire our trainers directly rather than broker freelancers, so certification,
                                insurance and service standards are verified on every booking.
                            </p>
                            <ul className="flex flex-wrap gap-2">
                                {['Your location', 'Your fitness goals', 'Your schedule'].map((item) => (
                                    <li key={item} className="flex items-center gap-1.5 bg-white/10 rounded-full pl-2 pr-3 py-1 border border-white/15">
                                        <FaCheckCircle className="text-orange-400 text-xs shrink-0" />
                                        <span className="text-xs font-medium text-white/80">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {/* Image Section - decorative only, so hidden below lg to save a
                            full mobile fold. */}
                        <div className="relative hidden lg:block lg:h-87.5">
                            {/* Floating badge - Certified */}
                            <div className="absolute top-4 right-4 z-10 bg-white/10 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-white/20">
                                <div className="flex items-center gap-2">
                                    <div className="w-10 h-10 bg-linear-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                                        <FaCheckCircle className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-white/50">Certified</p>
                                        <p className="font-bold text-white/90">Trainers</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Floating badge - Clients */}
                            {/* <div className="absolute bottom-8 left-4 z-10 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl border border-white/50">
                                <div className="flex items-center gap-2">
                                    <div className="flex -space-x-2">
                                        {['🏃', '💪', '🎯'].map((emoji, i) => (
                                            <div key={i} className="w-8 h-8 rounded-full bg-linear-to-br from-orange-400 to-amber-400 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                                                {emoji}
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Join</p>
                                        <p className="font-bold text-gray-800">20K+ clients</p>
                                    </div>
                                </div>
                            </div> */}
                            
                            {/* Main image */}
                            <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl">
                                <Image
                                    src={s3Static + "personal-trainer/trainer-with-indian-client-tranparent.png"}
                                    fill
                                    style={{ objectFit: 'contain', objectPosition: 'top' }}
                                    alt="Personal Trainer Singapore - StringsSG"
                                    sizes="50vw"
                                />
                            </div>
                        </div>
                    </div>
                    </div> 
                </section>                
                {/* 4. Why Choose Us */}
                <PersonalTrainerWhyUs />   
                {/* 7. Top Trainers */}
                <section id="top-trainers-singapore" className='relative bg-slate-900 overflow-hidden'>
                    {/* Mesh gradient blobs */}
                    <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/8 rounded-full blur-3xl" />
                    
                    <div className='relative z-10 py-10 max-w-7xl mx-auto px-3'>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                                <span className="text-sm">🏆</span>
                                <span className="text-xs font-medium text-white/70">Meet Our Team</span>
                            </div>
                            <h2 className='text-2xl md:text-4xl font-extrabold text-white tracking-tight'>
                                Meet Our Certified Personal Trainers in Singapore
                            </h2>
                        </div>
                        {/* All 12 trainers are kept for SEO/content depth, but on mobile they
                            render as a horizontal snap-scroll carousel instead of 12 stacked
                            rows (which alone cost ~4 mobile folds). Grid resumes at sm. */}
                        {/* Skips the trainers already shown in the hero strip so the
                            same faces don't appear twice on one page. */}
                        {/* Carousel only here: the hub has many other sections, so a full vertical
                            list would cost several folds. Pages where the trainer list is the
                            main content use the default stacked layout. */}
                        <TopTrainersSection spList={spList.slice(HERO_TRAINER_COUNT)} layout='carousel'/>
                        
                        <div className='text-center pt-5'>
                            <Link href="/sg/personal-trainer/trainer-list/Singapore"
                                prefetch={false} className='px-8 py-4 text-lg text-white bg-linear-to-tr from-orange-600 to to-amber-400 rounded-full'>
                                    View all Trainers
                            </Link>
                        </div> 
                    </div>          
                </section>                

                <section className='mx-auto'>                 
                    {/* Comparison Table */}
                    <div className='relative bg-slate-900 py-10'>
                        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl" />
                        <div className='relative z-10 max-w-5xl mx-auto px-3'>
                            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 mx-auto w-fit">
                                <span className="text-sm">📊</span>
                                <span className="text-xs font-medium text-white/70">Comparison</span>
                            </div>
                            <h3 className='text-xl md:text-2xl font-extrabold text-white mb-6 text-center tracking-tight'>
                                How StringsSG Compares
                            </h3>
                            <div className='overflow-x-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md'>
                                <table className='w-full text-xs md:text-sm border-collapse'>
                                    <thead>
                                        <tr className='bg-linear-to-r from-orange-500 to-amber-500 text-white'>
                                            <th className='py-2 px-2 md:py-3 md:px-4 text-left font-semibold'>Feature</th>
                                            <th className='py-2 px-2 md:py-3 md:px-4 text-center font-semibold'>StringsSG</th>
                                            <th className='py-2 px-2 md:py-3 md:px-4 text-center font-semibold'>Gyms</th>
                                            <th className='py-2 px-2 md:py-3 md:px-4 text-center font-semibold'>Mobile Trainers</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { feature: 'Free Consultation', stringssg: '✅ Yes', gym: '❌ Paid', mobile: '❌ Paid' },
                                            { feature: 'Transparent Pricing', stringssg: '✅ On website', gym: '⚠️ Unclear', mobile: '⚠️ Not public' },
                                            { feature: 'Gym Membership', stringssg: '✅ Not needed', gym: '❌ $170+/mo', mobile: '✅ Not needed' },
                                            // Trimmed from 7 rows to the 4 highest-intent differentiators.
                                            // Location flexibility, certification and nutrition guidance are
                                            // each already covered by PersonalTrainerWhyUs above.
                                            { feature: 'Employment Model', stringssg: '✅ Full-time', gym: '⚠️ Mixed', mobile: '❌ Freelance' }
                                        ].map((row, i) => (
                                            <tr key={i} className={`${i % 2 === 0 ? 'bg-white/5' : 'bg-white/2'} border-b border-white/5`}>
                                                <td className='py-2 px-2 md:py-3 md:px-4 font-medium text-white/80'>{row.feature}</td>
                                                <td className='py-2 px-2 md:py-3 md:px-4 text-center text-green-400 font-medium'>{row.stringssg}</td>
                                                <td className='py-2 px-2 md:py-3 md:px-4 text-center text-white/40'>{row.gym}</td>
                                                <td className='py-2 px-2 md:py-3 md:px-4 text-center text-white/40'>{row.mobile}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className='text-2xs text-white/30 mt-2 text-center'>
                                * Based on general Singapore market observations, 2026.
                            </p>
                        </div>
                    </div>
                </section>   

                {/* 3. Customer Reviews */}
                <TrainerCustomerReviews testimonials={props.testimonials}/>                             
                {/* 2. Value Proposition */}
                <TrainerValueProposition />   

                <section className="relative pt-10 pb-4 bg-slate-900 overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/8 rounded-full blur-3xl" />
                    <div className="relative z-10 max-w-6xl mx-auto px-4">
                        <div className="text-center mb-4">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-3">
                                <span className="text-sm">📍</span>
                                <span className="text-xs font-medium text-white/70">Trainer Map</span>
                            </div>
                            <h2 className="text-2xl font-extrabold text-white tracking-tight">
                                Personal Trainers Near You — {spListMap.length} Trainers Across Singapore
                            </h2>
                            <p className="text-sm text-white/40 mt-1">
                                Tap the markers on the map to view trainer details
                            </p>
                        </div>
                        <div id="map" className="h-72 md:h-112 rounded-2xl shadow-2xl mt-4 border border-white/10"></div>
                    </div>
                </section>

                <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={2026} />       

                {/* 5. How to Find a Trainer - SEO/YMYL optimized process guide */}
                {/* <HowToFindTrainerSection />                              */}

                {/* <TrainerByExperience/> */}

                {/* <div className='py-10 px-2'>
                    <h2 className='text-2xl md:text-4xl text-gray-900 font-bold text-center uppercase'>
                        Trainer By Type
                    </h2>
                    <TrainerByType/>   
                </div> */}

                {/* <div className='px-2'>
                    <h2 className='text-2xl md:text-4xl text-gray-900 font-bold text-center uppercase'>
                        Trainer By Fitness Goal
                    </h2>
                    <TrainerByFitnessType/>   
                </div>          */}

                {/* 6. Training Structure */}
                {/* <PersonalTrainerStructure /> */}
                
                {/* Cost comparison, buyer's guide, objections & cross-links */}
                <PtCostAndChoosing />

                {/* 6. Success Stories */}
                <SuccessStories />    
                
                {/* <div className='max-w-5xl mx-auto py-10 px-2 text-sm'>
                    <h3 className='text-xl md:text-2xl text-center uppercase'>
                        <strong>10 Benefits Of Personal Training</strong>
                    </h3>
                    <div className='py-4 grid grid-cols-1 md:grid-cols-2 space-y-2'>
                        <div className='space-y-2 leading-7'>
                            <p><strong>Individual Focus - </strong>
                                Every person has a varied level of fitness and distinct food habits and fitness goals. A trainer can design a program that is tailored to your needs and that you can long-term implement into your lifestyle.
                            </p>
                            <p className=''><strong>Motivation - </strong>
                                Having a trainer by your side will help push you through your training program and experience the adventure with you when you need motivation and encouragement.
                            </p>
                            <p><strong>Confidence - </strong>
                                You may find working out in the gym intimidating. Develop confidence by practicing exercises, using equipment, and navigating the gym with the help of a personal trainer. Even better, an ego boost during exercise might encourage higher feelings of self-efficacy and confidence, which can support long-term adherence to an exercise regimen.
                            </p>
                        </div>
                        <div className='relative w-full h-96'>
                            <Image
                                src={s3Static + "personal-trainer/generated/nur-faridah-training-nisha.png"}
                                fill
                                style={{objectFit: 'cover'}}
                                alt='Personal trainer helps client'
                                placeholder="blur"
                                blurDataURL={s3Static + "placeholder.png"}
                                sizes="(max-width: 768px) 100vw, 33vw"
                            />
                        </div>
                    </div>
                    <div className='py-4 grid grid-cols-1 md:grid-cols-2 md:space-x-2 space-y-2'>
                        <div className='relative w-full h-96 hidden md:flex'>
                            <Image
                                src={s3Static + "personal-trainer-Singapore.jpg"}
                                fill
                                style={{objectFit: 'cover'}}
                                alt='Personal training diet'
                                placeholder="blur"
                                blurDataURL={s3Static + "placeholder.png"}
                                sizes="(max-width: 768px) 100vw, 33vw"
                            />
                        </div>
                        <div className='space-y-2 leading-7 md:pl-2'>
                            <p><strong>Sport-specific Training - </strong>
                                Will you be getting ready for a hiking trip or your first 5K? Are you trying to improve your golf game? Your trainer can create a fitness regimen tailored to your sport that can boost your performance and lower your risk of injury during the event.
                            </p> 
                            <p><strong>Consistency - </strong>
                                Adhering to the plan can be challenging at times. A trainer can help you stick to your workout commitment and conquer all of your potential excuses. When you are aware that someone is waiting for you at the gym, it is much tougher to skip.
                            </p>
                            <p><strong>Prevent Injuries - </strong>
                                If you lack experience, you might not be aware of the appropriate kind of exercise that can result in injuries. To learn the right exercise methods, it is worthwhile to hire a trainer. This can help you achieve better results and avoid unwanted accidents.
                            </p>
                        </div>
                        <div className='relative w-full h-96 md:hidden'>
                            <Image
                                src={s3Static + "personal-trainer-Singapore.jpg"}
                                fill
                                style={{objectFit: 'cover'}}
                                alt='Personal training diet'
                                placeholder="blur"
                                blurDataURL={s3Static + "placeholder.png"}
                                sizes="(max-width: 768px) 100vw, 33vw"
                            />
                        </div>
                    </div>
                    <div className='py-4 space-y-2'>
                        <div className='space-y-2 leading-7'>                   
                            <p className=''><strong>Clarity - </strong>
                                Having a trainer by your side will help push you through your training program and experience the adventure with you when you need motivation and encouragement.
                            </p>
                            <p><strong>Training With Medical Conditions - </strong>
                                Exercise is helpful for managing or avoiding several common chronic illnesses, such as diabetes, heart disease, and high blood pressure. With a medical condition, exercise calls for additional safety measures. A qualified trainer who has expertise working with clients who have chronic diseases may create a plan that guarantees your safety and gives you a satisfying workout.
                            </p>
                            <p><strong>Gracefully Aging - </strong>
                                As we get older, our bodies alter. Perhaps you&apos;ve stopped seeing results, or the routines you used to undertake no longer suit your physique. Your program can be modified or tailored by a trainer to help you stay functional and strong as you age.
                            </p>
                            <p><strong>Fun - </strong>
                            Exercise may be fun, despite popular belief. A smart personal trainer can make working out enjoyable and beneficial. Group or buddy training can be a terrific method to make exercise more enjoyable, socialize exercise, and save money on a trainer&apos;s services. And all it takes to get greater enjoyment out of your workouts is working with a trainer you respect and like.
                            </p>
                        </div>
                    </div>
                </div> */}
                {/* <div className='my-10 shadow max-w-5xl mx-auto'>
                    <div className='py-4 px-4 bg-slate-600 text-white'>
                        <h3 className='text-center'>
                            <strong>Where to find personal trainers in Singapore?</strong>
                        </h3>
                    </div>
                    <p className='px-2 text-sm leading-7 py-5'>
                        As per our analytics research data, dated 9th January 2026, it is much easier for you to find a wide variety of personal trainers in the Central region of 
                        Singapore area followed by East. The below graph represents the concentration of personal trainers in different regions of Singapore.
                    </p>
    
                    <div className="h-64 max-w-md mx-auto">
                        <h2 className="text-sm font-semibold text-center text-gray-700 mb-2">
                        Trainer Distribution Across Singapore Regions
                        </h2>
                        <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={trainerDensity}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="region" />
                            <YAxis />
                            <Tooltip formatter={(v) => [`${v}%`, 'Percent']} />
                            <Legend />
                            <Bar dataKey="trainerPercent" fill="#ff9933" name="Trainer Percent" />
                        </BarChart>
                        </ResponsiveContainer>
                    </div>
    
                    <div className='py-5 px-2 space-y-2 text-sm leading-7 mt-5'>
                        <h5 className=''>
                            Here are a few ways you can find a personal trainer or gym near you -
                        </h5>
                        <div className='space-y-2'>
                            <p>
                                <strong>StringsSG - </strong>StringsSG has a huge database of gyms and trainers in almost every location in Singapore. You can either open a request for a 
                                personal trainer or you can just browse through the list and search for personal trainers near your location.                            
                            </p>
                            <p>
                                <strong>Google - </strong>Jump onto Google to start searching and browsing gyms and trainers near you.
                            </p>
                            <p>
                                <strong>Referral - </strong>Sometimes our friends and relatives may know of a nice gym or personal trainer that they can recommend.
                            </p>
                            <p>
                                <strong>Near By Gym - </strong>Walk-in to a gym and talk to the sales person in charge about your desired fitness goal and they will provide you with the 
                                relevant personal trainer.
                            </p>
                        </div>
                    </div>
                    <div className='p-2 text-sm leading-7'>
                        <p>
                            Find personal training services near you with the map based <q>Personal Trainer Locator</q>.
                        </p>
                    </div>   
                    <div className='text-center py-5'>
                        <Link href="/sg/personal-trainer/personal-trainer-finder-tool" prefetch={false} className='px-3 py-2 border text-orange-600'>
                                Personal Trainer Locator
                        </Link>
                    </div>
                </div> */}

                {/* <div className='shadow max-w-5xl mx-auto my-5'>
                    <div className='py-4 px-4 bg-amber-600 text-white'>
                        <h3 className='font-bold text-center'>
                            Considerations before getting personal training from a Gym
                        </h3>
                    </div>
                    <div className='p-4 grid md:grid-cols-2 md:gap-x-1 space-y-5 md:space-y-0'> 
                        <div className='text-sm leading-7 space-y-2'>
                            <p className=''>
                                <span className='font-semibold'>
                                    If you are looking for personal training at a gym, these are the questions you must ask yourself:
                                </span>
                                <br/>- How far is the gym from your house and what is the commuting time?
                                <br/>- Does the gym have a good brand and reputation?
                                <br/>- What are the opening hours of the gym?
                                <br/>- How crowded can the gym get and will it affect your personal training program?
                            </p>
                            <p>Gyms may require you to get a gym membership before you can get personal training, which also comes with a cost.</p>
                            <p>
                                With Singapore being one of the most expensive city to live in, the average monthly charges of a gym membership is around $170.
                            </p>                       
                        </div>
                        <div className='relative w-full h-80 md:h-full'>
                            <Image
                                src={s3Static + "blog/health+and+fitness/gym.jpg"}
                                fill
                                style={{objectFit: 'cover', objectPosition: 'center'}}    
                                placeholder='blur'
                                blurDataURL={s3Static + "placeholder.png"}
                                alt='Affordable Personal Training Gym - StringsSG'
                                sizes="(max-width: 768px) 100vw, 33vw"
                            />          
                        </div>
                    </div>
                    <div className='text-center py-10'>
                        <Link href="/sg/personal-trainer/cheap-gym-membership-singapore" prefetch={false} className='px-3 py-2  text-orange-600 border'>
                                Affordable Personal Training Gym
                        </Link>
                    </div>
                </div>        */}

                {/* 10. FAQs */}
                <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                    <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                    <div className='relative z-10 max-w-5xl mx-auto px-4'>
                        <DarkFaq 
                            title="Personal Trainer Singapore — FAQs"
                            questionsAnswers={ptSingaporeFaqs} 
                        />
                    </div>
                </section>
                
                {/* 9. Benefits CTA */}
                {/* <section className="py-8 md:py-16 bg-linear-to-b from-white to-gray-50">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <p className='text-sm md:text-base text-gray-500 mb-3 md:mb-4'>Learn the benefits of hiring a personal trainer</p>
                        <Link 
                            href="/blogs/why-you-need-personal-trainer-singapore" 
                            className='inline-flex items-center gap-2 py-3 px-6 md:py-4 md:px-8 rounded-full border-2 border-orange-500 text-orange-600 text-sm md:text-base font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300'
                        >
                            10 Benefits Of Personal Training
                            <BsArrowRight />
                        </Link>
                    </div>
                </section> */}
                
                {/* Neighborhood Selection */}
                {/* <section className="py-16 px-4 bg-gray-50">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className='text-xl md:text-3xl text-center uppercase'>
                                <strong>Personal Trainers in your Neighborhood</strong>
                            </h2>
                        </div>
                        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                {[...localityList].filter((locale) => {if(locale != "Singapore") return true}).map((locality, i) =>
                                <div key={i} className="text-center">
                                    <Link
                                    href={`/sg/personal-trainer/trainer-list/${encodeURIComponent(locality)}`}
                                    prefetch={false}
                                    className="inline-block w-full py-3 px-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-600 hover:bg-orange-100 hover:border-orange-300 hover:text-orange-700 transition-all duration-200 font-medium text-sm"
                                    >
                                    {locality}
                                    </Link>
                                </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section> */}
            </RequestProvider>
            
            {/* ─── Trusted Partners ─── */}
            {/* <TrustedPartners /> */}
            {/* Quick Links */}
            {/* <TrainerQuickLinks /> */}
            
            {/* WhatsApp FAB */}
            {/* <motion.div 
                className='fixed bottom-3 right-3 z-10'
                initial={{ y: 50 }}
                animate={isShowBottomButtons ? { y: 0 } : { y: 90 }}
                transition={{ type: "spring", stiffness: 100, ease: "easeInOut", duration: 0.3 }}
            >
                <a 
                    href={`https://wa.me/6593512695?text=${encodeURIComponent("I would like to request for a free personal training session.")}`}
                    target="_blank"
                    rel="noreferrer"
                    className='flex items-center text-white bg-green-600 p-4 rounded-full shadow-lg hover:shadow-xl hover:bg-green-700 transition-all'
                    aria-label="Chat with us on WhatsApp"
                    title="Chat with us on WhatsApp"
                >
                    <FaWhatsapp className='text-4xl' />
                </a>
            </motion.div> */}
            
            {/* Modals below*/}

            {isMapVisible &&
                <Script
                    id="google-maps"
                    src={`https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&language=en&libraries=places,marker&callback=initMap&loading=async`}
                    strategy="afterInteractive"
                />
            }          
        </>
    )
}
